import CmsBaseClass from "@/helpers/cms/CmsBaseClass.js";
import CmsLink from "@/helpers/cms/CmsLink.js";
import StyleHelper from "@/helpers/StyleHelper.js";
import store from "@/store.js";
import { getShortCodeFromFull, getTranslations } from '@/../vue-components/helpers/I18nPluginHelper.js';
import EditorColorGradient from "@/helpers/cms/EditorColorGradient.js";
import CompatibilityHelper from "@/helpers/CompatibilityHelper.js";
import {tr} from "../../../vue-components/helpers/i18n.js";

export default class CmsWebsite extends CmsBaseClass {
    static TYPES = {
        WEBSITE: `website`,
        KIOSK: `kiosk`
    }

    dealerid;
    type = CmsWebsite.TYPES.WEBSITE;
    name = {
        en: `New Website`
    };
    domain;
    alias;
    visibility = `private`;
    settings;
    integrations;
    legal;
    meta;
    design;
    accessibility;
    body;
    menu;
    pages;
    footer;
    integrations;
    map;
    style;

    get autoGenerateId() {
        return false;
    }

    get ignoredFields() {
        return [`styleVariables`, `elements`, `data`, `items`, `types`, `map`];
    }

    constructor(data) {
        super();

        this.elements = {};

        this.loadSave(data);

        this.accessibility = this.mergeDefaults(this.accessibility, {
            wheelchair: {
                topMargin: `880px`,
                background: `#3D454A`,
                placeholder: {
                    "src": ``
                }
            }
        });
        this.integrations = this.mergeDefaults(this.integrations, {
            map: {
                enabled: false,
                mapId: null
            }
        });
        this.settings = this.mergeDefaults(this.settings, {
            languageModalShowFlags: true
        });
        this.settings.analytics = this.mergeDefaults(this.settings.analytics, {
            googleTags: ``,
            metaPixel: ``
        });
        this.settings.testing = this.mergeDefaults(this.settings.testing, {
            enabled: false,
            testersMessage: {en: ``}
        });
        this.aliases = this.mergeDefaults(this.aliases, [
            {domain: this.domain, env: `LIVE`}
        ]);
        this.routing = this.mergeDefaults(this.routing, {
            redirects: []
        });

        if (!this.body.design.style.containerType) {
            this.body.design.style.containerType = `contained`;
        }
    }

    onSaveLoaded(data) {
        super.onSaveLoaded(data);
        this.initializeColorSchemes();
    }

    initializeColorSchemes() {
        if (!this.design || !this.design.colorSchemes) return;

        for (let scheme of this.design.colorSchemes) {
            // Only convert background if it's a gradient object (type: 'linear')
            if (scheme.background && typeof scheme.background === `object` && scheme.background.type === `linear` && !scheme.background.toCss) {
                scheme.background = new EditorColorGradient(scheme.background);
            }
        }
    }

    initWebsite() {
        // Update Navigation Links
        let links = [];

        for (let link of this.menu.links) {
            links.push(new CmsLink(link));
        }
        this.menu.links = links;

        if (this.menu.block) {
            this.menu.block = window.cmsFactory.new(this.menu.block);
        }
        if (this.footer.block) {
            this.footer.block = window.cmsFactory.new(this.footer.block);
        }
    }

    toJSON() {
        let data = super.toJSON();
        data.pages = data.pages.map(p => p.id);
        return data;
    }

    getColor(colorOrVariant) {
        if (typeof colorOrVariant === `object` && colorOrVariant !== null && colorOrVariant.type === `linear`) {
            const tempGradient = new EditorColorGradient(colorOrVariant);
            return tempGradient.toCss();
        }

        colorOrVariant = CompatibilityHelper.checkColor(colorOrVariant);

        if (this.design.colors && this.design.colors.hasOwnProperty(colorOrVariant)) {
            return this.design.colors[colorOrVariant];
        }

        return colorOrVariant;
    }

    getHomePage() {
        return this.pages.find(p => p.homePage === true) || this.pages[0];
    }

    getMapPage() {
        return this.pages.find(p => p.type === `map`);
    }

    applyStyle() {
        this.styleVariables = {};
        this.elements.favicon = document.getElementById(`favicon`);
        if (this.elements.favicon) {
            this.elements.favicon.href = this.design.favicon.src;
        }

        if (!this.design.fonts.headlines) {
            this.design.fonts.headlines = this.design.fonts.titles;
        }
        if (!this.design.fonts.buttons) {
            this.design.fonts.buttons = this.design.fonts.body;
        }

        for (let type of this.fontTypes) {
            const fontStyle = this.design.fonts[type];
            if (fontStyle.font && store.getters[`googleFonts/loadedFonts`].indexOf(fontStyle.fontFamily) < 0) {
                let variant = ``;
                if (fontStyle.weightRange?.start) {
                    variant = `:wght@${fontStyle.weightRange.start}..${fontStyle.weightRange.end}`;
                }
                let link = document.createElement(`link`);
                link.setAttribute(`rel`, `stylesheet`);
                link.setAttribute(`type`, `text/css`);
                link.setAttribute(`href`, `https://fonts.googleapis.com/css2?family=${fontStyle.fontFamily}${variant}&display=swap`);
                document.head.appendChild(link);
                store.commit(`googleFonts/setLoaded`, fontStyle.fontFamily);
            }
        }

        if (this.style.customCSS) {
            let link = document.createElement(`style`);
            link.innerHTML = this.style.customCSS;
            document.body.appendChild(link);
        }

        /*
        CSS Custom Variables
         */
        // Fonts
        this.setFontStyle(`headlines`, this.design.fonts.headlines);
        this.setFontStyle(`titles`, this.design.fonts.titles);
        this.setFontStyle(`body`, this.design.fonts.body);
        this.setFontStyle(`buttons`, this.design.fonts.buttons);

        // Body
        this.setCssProperty(`--page-width`, `${StyleHelper.getResponsiveStyleProp(this.body.design.style, `maxWidth`)}px`);
        this.setCssProperty(`--body-color`, StyleHelper.getResponsiveStyleProp(this.body.design.style, `color`));
        this.setCssProperty(`--body-background`, StyleHelper.getResponsiveStyleProp(this.body.design.style, `background`));

        // Fonts
        this.setCssProperty(`--font-base-size`, this.design.fonts.baseSize);
        this.setCssProperty(`--font-scale`, this.design.fonts.scale);

        // Color Scheme
        for (let variant in this.design.colors) {
            this.setCssProperty(`--color-${variant}`, this.design.colors[variant]);
        }

        this.setCssProperty(`--border-radius`, this.design.borders.radius);
        this.setCssProperty(`--border-width`, this.design.borders.width);
        this.setCssProperty(`--border-color`, this.design.borders.color);

        // Menu
        if (this.menu.block) {
            if (this.menu.block.mediaProxy.orientation === `horizontal`) {
                this.setCssProperty(`--menu-width`, `0px`);
                this.setCssProperty(`--menu-height`, `${this.menu.block.mediaProxy.size}px`);
                if (this.menu.block.mediaProxy.anchor === `top`) {
                    this.setCssProperty(`--page-padding-top`, `${this.menu.block.mediaProxy.size}px`);
                } else {
                    this.setCssProperty(`--page-padding-bottom`, `${this.menu.block.mediaProxy.size}px`);
                }
            } else {
                this.setCssProperty(`--menu-width`, `${this.menu.block.mediaProxy.size}px`);
                this.setCssProperty(`--menu-height`, `0px`);
                if (this.menu.block.mediaProxy.anchor === `left`) {
                    this.setCssProperty(`--page-padding-left`, `${this.menu.block.mediaProxy.size}px`);
                } else {
                    this.setCssProperty(`--page-padding-right`, `${this.menu.block.mediaProxy.size}px`);
                }
            }
        }

        // Cards
        this.setCssProperty(`--cards-box-shadow`, StyleHelper.getResponsiveStyleProp(this.design.cards.style, `boxShadow`));
        this.setCssProperty(`--cards-border-radius`, StyleHelper.getResponsiveStyleProp(this.design.cards.style, `borderRadius`));

        store.state.websiteStyleVersion++;
    }

    get fontTypes() {
        return [`titles`, `body`, `headlines`, `buttons`];
    }

    setFontStyle(name, value) {
        if (value) {
            this.setCssProperty(`--${name}-font`, value.fontFamily);
            this.setCssProperty(`--${name}-font-weight`, value.fontWeight);
            this.setCssProperty(`--${name}-letter-spacing`, value.letterSpacing);
        }
    }

    makeI18nText(inTexts) {
        // Convert input to proper translation object using I18nHelper
        const translations = getTranslations(inTexts);
        if (!translations) {
            return {};
        }
        return translations;
    }

    getColorSchemeVariables(colorScheme) {
        let style = {};

        if (colorScheme) {
            style[`--scheme-bg`] = this.getColor(colorScheme.background);
            style[`--scheme-title-color`] = this.getColor(colorScheme.title);
            style[`--scheme-paragraph-color`] = this.getColor(colorScheme.paragraph);
            style[`--scheme-button-bg-color`] = this.getColor(colorScheme.buttonBG);
            style[`--scheme-button-color`] = this.getColor(colorScheme.buttonColor);
            style[`--scheme-button-outline-bg-color`] = this.getColor(colorScheme.outlineBG);
            style[`--scheme-button-outline-color`] = this.getColor(colorScheme.outlineColor);
            style[`--scheme-button-link-color`] = this.getColor(colorScheme.linkColor);
        }
        return style;
    }

    getColorScheme(themeId = `default`) {
        return cmsWebsite.design.colorSchemes.find(c => c.id === themeId);
    }

    getDefaultBlockContainer() {
        return cmsWebsite.body.design.style.containerType;
    }

    setCssProperty(name, value) {
        if (value && value !== ``) {
            this.styleVariables[name] = value;
        }
    }

    getPreviewUrl(page) {
        let env = window.env.VUE_APP_ENV.toUpperCase();
        if (env === `NEXT`) {
            env = `LIVE`;
        } else if (env === `DEVNEXT`) {
            env = `DEV`;
        }

        const alias = this.aliases.find(a => a.env === env);
        const permalink = page ? tr(page.permalink) : ``;
        if (alias) {
            return `https://${alias.domain}${permalink ? `/${permalink}` : ``}`;
        }

        if (window.env.VUE_APP_ENV === `local` && page) {
            return `${window.env.VUE_APP_URLS__CMS}${this.getLocalPreviewRoute(page)}`
        }

        return `https://${this.domain}${permalink ? `/${permalink}` : ``}`;
    }

    getLocalPreviewRoute(page) {
        return `/cms/preview/website/${this.id}/${page?.id}`
    }
}
